import { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { brl } from "@/lib/format";
import {
  RefreshCw, Loader2, CheckCircle2, AlertTriangle,
  ChevronDown, ChevronUp, Users, DollarSign, Database,
} from "@/components/icons";

export default function ImportarCarteiraSankhya() {
  const { user } = useAuth();
  const [previa, setPrevia] = useState(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resultadoAplicar, setResultadoAplicar] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [carregandoLotes, setCarregandoLotes] = useState(false);
  const [desfazendoId, setDesfazendoId] = useState(null);
  const [expand, setExpand] = useState({});

  if (user?.role !== "admin") return <Navigate to="/" replace />;

  const toggle = (k) => setExpand((e) => ({ ...e, [k]: !e[k] }));

  const carregarPrevia = async () => {
    setCarregandoPrevia(true); setResultadoAplicar(null);
    try {
      const { data } = await api.get("/admin/carteira-sankhya/previa");
      setPrevia(data);
      if (!data.resumo.sankhya_configurado) {
        toast.warning("Sankhya não está configurado neste ambiente. A prévia mostra apenas o que já está sincronizado em sankhya_faturamento.");
      } else if (!data.resumo.enriquecimento_ok) {
        toast.warning("Não foi possível enriquecer os CNPJs completos no Sankhya. Clientes sem CNPJ completo ficam na lista separada.");
      } else {
        toast.success(`Prévia pronta: ${data.resumo.total_novos_a_criar} para criar, ${data.resumo.total_existentes_a_marcar} para marcar.`);
      }
    } catch (e) {
      toast.error(errMsg(e) || "Falha ao carregar prévia");
    } finally {
      setCarregandoPrevia(false);
    }
  };

  const aplicar = async () => {
    if (!previa) return;
    if (!window.confirm(
      `Confirmar? Serão CRIADOS ${previa.resumo.total_novos_a_criar} clientes e MARCADOS ${previa.resumo.total_existentes_a_marcar}. Essa ação é reversível pela "Desfazer" abaixo.`
    )) return;
    setAplicando(true);
    try {
      const { data } = await api.post("/admin/carteira-sankhya/aplicar");
      setResultadoAplicar(data);
      toast.success(`Lote aplicado. Criados: ${data.totais.criados}, marcados: ${data.totais.marcados}.`);
      await carregarLotes();
      setPrevia(null);
    } catch (e) {
      toast.error(errMsg(e) || "Falha ao aplicar");
    } finally {
      setAplicando(false);
    }
  };

  const carregarLotes = async () => {
    setCarregandoLotes(true);
    try {
      const { data } = await api.get("/admin/carteira-sankhya/lotes");
      setLotes(data.items || []);
    } catch (e) {
      toast.error(errMsg(e) || "Falha ao carregar lotes");
    } finally {
      setCarregandoLotes(false);
    }
  };

  const desfazer = async (lote_id) => {
    if (!window.confirm(`Desfazer o lote ${lote_id.slice(0, 8)}…? Clientes criados por este lote vão para a lixeira; marcações feitas por este lote serão revertidas.`)) return;
    setDesfazendoId(lote_id);
    try {
      const { data } = await api.post(`/admin/carteira-sankhya/desfazer/${lote_id}`);
      toast.success(`Removidos: ${data.removidos}, marcações revertidas: ${data.marcacao_revertida}.`);
      await carregarLotes();
    } catch (e) {
      toast.error(errMsg(e) || "Falha ao desfazer");
    } finally {
      setDesfazendoId(null);
    }
  };

  return (
    <div data-testid="cart1-page">
      <PageHeader
        title="Importar carteira ativa do Sankhya"
        subtitle="Puxa para o CRM os clientes com faturamento nos últimos 12 meses, atribuindo cada um ao vendedor via sankhya_vinculos. Prévia obrigatória — nada é gravado sem o passo Aplicar."
      />

      <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)] mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={carregarPrevia}
            disabled={carregandoPrevia}
            data-testid="cart1-previa-btn"
            className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60 flex items-center gap-2"
          >
            {carregandoPrevia ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Carregar prévia (12 meses)
          </button>
          {previa && (
            <button
              onClick={aplicar}
              disabled={aplicando || previa.resumo.aplicar_bloqueado || previa.resumo.total_novos_a_criar + previa.resumo.total_existentes_a_marcar === 0}
              data-testid="cart1-aplicar-btn"
              title={previa.resumo.aplicar_bloqueado ? previa.resumo.aplicar_bloqueado_motivo : ""}
              className="h-10 px-5 rounded-lg bg-ok text-white text-sm font-semibold hover:brightness-110 disabled:opacity-60 flex items-center gap-2"
            >
              {aplicando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Aplicar lote
            </button>
          )}
          {previa?.resumo?.aplicar_bloqueado && (
            <div className="w-full lg:w-auto flex items-center gap-2 px-3 py-2 rounded-lg bg-warn-soft text-warn text-xs font-medium" data-testid="cart1-trava-aviso">
              <AlertTriangle className="w-4 h-4" />
              {previa.resumo.aplicar_bloqueado_motivo}
            </div>
          )}
          <button
            onClick={carregarLotes}
            disabled={carregandoLotes}
            data-testid="cart1-lotes-btn"
            className="h-10 px-3 rounded-lg border border-input text-navy text-sm font-medium hover:bg-slate-50 disabled:opacity-60 flex items-center gap-2"
          >
            {carregandoLotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            Histórico de lotes
          </button>
        </div>

        {previa && (
          <div className="mt-5 space-y-4" data-testid="cart1-previa">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <KPI label="Parceiros 12m" value={previa.resumo.codparcs_ativos} icon={<Database className="w-4 h-4" />} />
              <KPI label="Novos a criar" value={previa.resumo.total_novos_a_criar} tone="ok" icon={<CheckCircle2 className="w-4 h-4" />} testid="cart1-kpi-novos" />
              <KPI label="A marcar" value={previa.resumo.total_existentes_a_marcar} tone="info" />
              <KPI label="Sem dono" value={previa.resumo.total_sem_dono} tone="warn" icon={<AlertTriangle className="w-4 h-4" />} />
              <KPI label="Sem CNPJ completo" value={previa.resumo.total_sem_cnpj_completo} tone="warn" />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-semibold text-navy flex items-center gap-2"><Users className="w-4 h-4" /> Por vendedor</div>
              {previa.por_vendedor.length === 0 && (
                <div className="text-xs text-muted-foreground italic">Nenhum vendedor com parceiros faturados nos últimos 12 meses.</div>
              )}
              {previa.por_vendedor.map((s) => {
                const open = !!expand[`v_${s.user_id}`];
                return (
                  <div key={s.user_id} className="rounded-xl border border-black/5 overflow-hidden" data-testid={`cart1-slot-${s.user_id}`}>
                    <button
                      onClick={() => toggle(`v_${s.user_id}`)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
                    >
                      <div>
                        <div className="font-semibold text-navy">{s.user_name}</div>
                        <div className="text-xs text-muted-foreground">codvend {s.codvend} · Novos {s.novos_a_criar.length} · Marcar {s.existentes_a_marcar.length} · Já OK {s.ja_ok.length}</div>
                      </div>
                      {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {open && (
                      <div className="px-4 py-3 space-y-3">
                        <ListaItens titulo="Novos a criar" tone="ok" items={s.novos_a_criar} />
                        <ListaItens titulo="Existentes a marcar (status + ja_cliente_mpires)" tone="info" items={s.existentes_a_marcar} />
                        {s.ja_ok.length > 0 && (
                          <ListaItens titulo="Já OK (nenhuma mudança)" tone="muted" items={s.ja_ok} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {previa.sem_dono.length > 0 && (
              <details className="rounded-xl border border-warn/30 bg-warn-soft p-3" data-testid="cart1-sem-dono">
                <summary className="cursor-pointer text-sm font-semibold text-warn flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Sem dono — codvend sem vínculo no CRM ({previa.sem_dono.length})
                </summary>
                <TabelaSimples items={previa.sem_dono} extra={(x) => `codvend ${x.codvend}${x.codvend_nome ? ` · ${x.codvend_nome}` : ""}`} />
              </details>
            )}

            {previa.sem_cnpj_completo.length > 0 && (
              <details className="rounded-xl border border-warn/30 bg-warn-soft p-3" data-testid="cart1-sem-cnpj">
                <summary className="cursor-pointer text-sm font-semibold text-warn flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Sem CNPJ completo ({previa.sem_cnpj_completo.length})
                </summary>
                <TabelaSimples items={previa.sem_cnpj_completo} />
              </details>
            )}

            {previa.diagnostico && (
              <details className="rounded-xl border border-black/5 bg-slate-50 p-3" data-testid="cart1-diagnostico">
                <summary className="cursor-pointer text-sm font-semibold text-navy flex items-center gap-2">
                  <Database className="w-4 h-4" /> Diagnóstico (admin)
                </summary>
                <Diagnostico diag={previa.diagnostico} />
              </details>
            )}
          </div>
        )}

        {resultadoAplicar && (
          <div className="mt-5 rounded-xl border border-ok/30 bg-ok-soft p-4" data-testid="cart1-aplicado">
            <div className="text-sm font-semibold text-ok flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Lote aplicado</div>
            <div className="text-xs text-navy mt-1">Lote <span className="font-mono">{resultadoAplicar.lote_id}</span></div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-3">
              <KPI label="Criados" value={resultadoAplicar.totais.criados} tone="ok" />
              <KPI label="Marcados" value={resultadoAplicar.totais.marcados} tone="info" />
              <KPI label="Já existentes (pulados)" value={resultadoAplicar.totais.ja_existentes_pulados} tone="muted" />
              <KPI label="Sem dono (fora)" value={resultadoAplicar.totais.sem_dono} tone="warn" />
              <KPI label="Erros" value={resultadoAplicar.totais.erros} tone={resultadoAplicar.totais.erros ? "warn" : "muted"} />
            </div>
          </div>
        )}
      </div>

      {lotes.length > 0 && (
        <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)]" data-testid="cart1-lotes">
          <div className="text-sm font-semibold text-navy mb-3 flex items-center gap-2"><Database className="w-4 h-4" /> Histórico de lotes</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-2 pr-3">Iniciado em</th>
                  <th className="py-2 pr-3">Admin</th>
                  <th className="py-2 pr-3">Lote</th>
                  <th className="py-2 pr-3">Situação</th>
                  <th className="py-2 pr-3">Criados</th>
                  <th className="py-2 pr-3">Marcados</th>
                  <th className="py-2 pr-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((l) => (
                  <tr key={l.lote_id} className="border-t border-black/5">
                    <td className="py-2 pr-3 font-mono">{(l.iniciado_em || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="py-2 pr-3">{l.admin_nome}</td>
                    <td className="py-2 pr-3 font-mono text-[10px]">{l.lote_id.slice(0, 8)}…</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        l.status === "concluido" ? "bg-ok/10 text-ok" :
                        l.status === "desfeito" ? "bg-slate-200 text-slate-600" :
                        "bg-warn/10 text-warn"
                      }`}>{l.status}</span>
                    </td>
                    <td className="py-2 pr-3">{l.totais?.criados ?? "—"}</td>
                    <td className="py-2 pr-3">{l.totais?.marcados ?? "—"}</td>
                    <td className="py-2 pr-3">
                      {l.status === "concluido" && (
                        <button
                          onClick={() => desfazer(l.lote_id)}
                          disabled={desfazendoId === l.lote_id}
                          data-testid={`cart1-desfazer-${l.lote_id}`}
                          className="h-8 px-3 rounded-md border border-input text-xs font-medium hover:bg-slate-50 disabled:opacity-60 inline-flex items-center gap-1"
                        >
                          {desfazendoId === l.lote_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
                          Desfazer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, tone = "muted", icon = null, testid = null }) {
  const map = {
    ok: "bg-ok-soft text-ok",
    warn: "bg-warn-soft text-warn",
    info: "bg-slate-50 text-navy",
    muted: "bg-slate-50 text-navy",
  };
  return (
    <div className={`rounded-xl p-3 ${map[tone]}`} data-testid={testid || undefined}>
      <div className="text-[11px] uppercase tracking-wider opacity-70 flex items-center gap-1">{icon}{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function ListaItens({ titulo, tone, items }) {
  if (!items || items.length === 0) return null;
  const toneCls = tone === "ok" ? "text-ok" : tone === "info" ? "text-navy" : "text-muted-foreground";
  return (
    <div>
      <div className={`text-xs font-semibold ${toneCls} mb-1`}>{titulo} ({items.length})</div>
      <TabelaSimples items={items} />
    </div>
  );
}

function TabelaSimples({ items, extra = null }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="py-1 pr-3">Nome</th>
            <th className="py-1 pr-3">Fantasia</th>
            <th className="py-1 pr-3">CNPJ</th>
            <th className="py-1 pr-3">Cidade</th>
            <th className="py-1 pr-3 text-right"><DollarSign className="w-3 h-3 inline" /> 12m</th>
            {extra && <th className="py-1 pr-3">Observação</th>}
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 100).map((x, i) => (
            <tr key={`${x.codparc}-${i}`} className="border-t border-black/5">
              <td className="py-1 pr-3">{x.nome || "—"}</td>
              <td className="py-1 pr-3">{x.nome_fantasia || "—"}</td>
              <td className="py-1 pr-3 font-mono">{x.cnpj || "—"}</td>
              <td className="py-1 pr-3">{x.cidade ? `${x.cidade}${x.uf ? "/" + x.uf : ""}` : "—"}</td>
              <td className="py-1 pr-3 text-right">{brl(x.valor_12m || 0)}</td>
              {extra && <td className="py-1 pr-3 text-warn">{extra(x)}</td>}
            </tr>
          ))}
          {items.length > 100 && (
            <tr><td colSpan={extra ? 6 : 5} className="py-1 text-center text-muted-foreground italic">… e mais {items.length - 100}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const MOTIVO_LABEL = {
  ok: "OK (CNPJ válido)",
  sem_cache: "Sem cadastro no cache",
  cnpj_vazio: "CGC_CPF vazio na TGFPAR",
  cnpj_pf: "Pessoa física (fora da importação)",
  cnpj_invalido: "CNPJ com dígito verificador inválido",
  codvend_sem_vinculo: "codvend sem vínculo no CRM",
  codvend_vinculo_sem_user_id: "Vínculo existe, mas sem vendedor atribuído",
};

function Diagnostico({ diag }) {
  const col = diag.colecoes || {};
  const mot = diag.motivos || {};
  const ex = diag.exemplos || {};
  const sqlEss = diag?.sql?.essencial || {};
  const sqlOpc = diag?.sql?.opcional || {};
  return (
    <div className="mt-3 space-y-3 text-xs">
      <div>
        <div className="font-semibold text-navy mb-1">Coleções (contagens)</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(col).map(([k, v]) => (
            <div key={k} className="rounded-md bg-white border border-black/5 p-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
              <div className="text-sm font-bold text-navy">{v}</div>
            </div>
          ))}
          <div className="rounded-md bg-white border border-black/5 p-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">cache_hits</div>
            <div className="text-sm font-bold text-navy">{diag.cache_hits ?? 0}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="font-semibold text-navy mb-1">Por que o parceiro não entrou (motivos)</div>
        <table className="w-full">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-3">Motivo</th>
              <th className="py-1 pr-3 text-right">Qtd</th>
              <th className="py-1 pr-3">Exemplos (codparc · codvend · CNPJ mascarado)</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(mot).map((k) => (
              <tr key={k} className="border-t border-black/5">
                <td className="py-1 pr-3">{MOTIVO_LABEL[k] || k}</td>
                <td className="py-1 pr-3 text-right font-semibold">{mot[k]}</td>
                <td className="py-1 pr-3 font-mono text-[11px]">
                  {(ex[k] || []).map((e, i) => (
                    <span key={i} className="inline-block mr-2">{e.codparc} · {e.codvend ?? "—"} · {e.cnpj_mascarado || "—"}</span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="font-semibold text-navy mb-1">SQL TGFPAR (só leitura, em lotes de 500)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <SqlBlock titulo="Essencial (CODPARC, CGC_CPF, TIPPESSOA, NOMEPARC, RAZAOSOCIAL)" d={sqlEss} />
          <SqlBlock titulo="Opcional (NOMEFANT + JOIN TSICID → cidade/UF)" d={sqlOpc} />
        </div>
      </div>
    </div>
  );
}

function SqlBlock({ titulo, d }) {
  return (
    <div className="rounded-md bg-white border border-black/5 p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{titulo}</div>
      <div className="text-xs mt-1">
        Lotes: <b>{d.lotes || 0}</b> · OK: <span className="text-ok font-semibold">{d.lotes_ok || 0}</span> · Erro: <span className="text-warn font-semibold">{d.lotes_erro || 0}</span>
      </div>
      {(d.erros || []).length > 0 && (
        <div className="mt-2 space-y-1">
          {(d.erros || []).map((e, i) => (
            <div key={i} className="rounded bg-warn-soft text-warn p-2 text-[11px] font-mono break-words">
              lote {e.lote_tamanho}: {e.mensagem}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

